import React, { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import CrudTable from "../../components/dashboard/table/CrudTable";
import CrudForm from "../../components/dashboard/form/CrudForm";
import { useCrud } from "../../hooks/useCrud";
import Swal from "sweetalert2";
import Breadcrumb from "../../components/Breadcrumbdashboard";

const generateSlug = (text) =>
  text.toString().toLowerCase().trim().replace(/\s+/g, "-");

export default function Brand() {
  const navigate = useNavigate();

  const {
    data: brands,
    loading,
    errors,
    createItem,
    updateItem,
    deleteItem,
  } = useCrud(
    `${process.env.REACT_APP_API_URL}/backend/brands`,
    ["brand_image"]
  );

  const [editingItem, setEditingItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const allowedFields = useMemo(
    () => ["name", "brand_image", "status"],
    []
  );


  const getFormData = useCallback(
    (item) => {
      if (!item)
        return {
          name: "",
          brand_image: null,
          status: 1,
        };

      return allowedFields.reduce((acc, field) => {
        acc[field] =
          field === "status"
            ? Number(item[field] ?? 1)
            : field === "brand_image"
            ? item[field] || null
            : item[field] || "";
        return acc;
      }, {});
    },
    [allowedFields]
  );

  const hasDuplicate = useCallback(
    (name, slug, id = null) =>
      brands.some(
        (b) =>
          (b.name.toLowerCase() === name.toLowerCase() ||
            b.slug.toLowerCase() === slug.toLowerCase()) &&
          b.id !== id
      ),
    [brands]
  );

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: { confirmButton: "btn btn-primary" },
      buttonsStyling: false,
    });
  }, []);

  const handleSubmit = useCallback(
    async (formData) => { 
      if (!formData.name.trim()) {
        showSwal("Error!", "Brand name is required.", "warning");
        return;
      }

      formData.slug = generateSlug(formData.name);

      if (hasDuplicate(formData.name, formData.slug, editingItem?.id)) {
        showSwal("Duplicate!", "Brand already exists.", "warning");
        return;
      }

      const dataToSend = {};

      allowedFields.forEach((field) => {
        if (field === "brand_image") {
          if (formData.brand_image instanceof File) {
            dataToSend.brand_image = formData.brand_image;
          }
        } else {
          dataToSend[field] = formData[field];
        }
      });

      dataToSend.slug = formData.slug;

      try {
        if (editingItem) {
          await updateItem(editingItem.id, dataToSend);
          showSwal("Updated!", "Brand updated successfully.", "success");
        } else {
          await createItem(dataToSend);
          showSwal("Created!", "Brand created successfully.", "success");
        }

        setModalOpen(false);
        setEditingItem(null);
      } catch (error) {
        showSwal("Error!", error.message || "Something went wrong.", "error");
      }
    },
    [allowedFields, editingItem, updateItem, createItem, hasDuplicate, showSwal]
  );

  const openModal = useCallback((item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback(
    (id) => {
      Swal.fire({
        title: "Are you sure?",
        text: "This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
         confirmButtonColor: "#0f5f58",
          cancelButtonText: "Cancel",
  cancelButtonColor: "#da4b23",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deleteItem(id);
            showSwal("Deleted!", "Brand deleted.", "success");
          } catch (error) {
            showSwal("Error!", "Delete failed.", "error");
          }
        }
      });
    }, 
    
    [deleteItem, showSwal]
  );

  const handleToggleStatus = useCallback(
    async (item) => {
      try {
        const updatedData = {
          name: item.name,
          slug: item.slug,
          status: item.status ? 0 : 1,
        };

        await updateItem(item.id, updatedData);
        showSwal("Success!", "Status updated.", "success");
      } catch {
        showSwal("Error!", "Failed to update status.", "error");
      }
    },
    [updateItem, showSwal]
  );

  const tableData = useMemo(
    () => brands.map((item, idx) => ({ ...item, sl: idx + 1 })),
    [brands]
  );

return (
  <DashboardWrapper>
    <div
      className="container-fluid"
      style={{
        marginTop: "10px",
        paddingTop: "0",
        paddingBottom: "0",
      }}
    >
      {/* TOP ROW */}
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          marginBottom: "8px",
          padding: "0 4px",
        }}
      >
        {/* Brands text - LEFT */}
        <h4
          className="mb-0"
          style={{
            fontSize: "20px",
            fontWeight: "500",
            color: "#374151",
          }}
        >
          Brands
        </h4>

        {/* Breadcrumb - RIGHT */}
        <div style={{ margin: 0 }}>
          <Breadcrumb />
        </div>
      </div>

      {/* TABLE CARD */}
      <div
        className="card shadow"
        style={{
          marginTop: "0",
          marginBottom: "10px",
        }}
      >
        {/* CARD HEADER */}
        <div
          className="card-header d-flex justify-content-start align-items-center"
          style={{
            padding: "8px 16px",
            minHeight: "50px",
          }}
        >
          {/* Create Brand button - LEFT */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => openModal()}
            style={{
              padding: "7px 16px",
              fontSize: "14px",
            }}
          >
            Create Brand
          </button>
        </div>

        {/* MODAL */}
        {modalOpen && (
          <div
            className="modal fade show"
            style={{
              display: "block",
              background: "rgba(0,0,0,0.5)",
            }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem ? "Edit Brand" : "Create Brand"}
                  </h5>

                  <button
                    className="btn-close"
                    onClick={closeModal}
                  />
                </div>

                <div className="modal-body">
                  <CrudForm
                    initialData={getFormData(editingItem)}
                    onSubmit={handleSubmit}
                    allowedFields={allowedFields}
                    fileFields={[
                      {
                        name: "brand_image",
                        multiple: false,
                      },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TABLE BODY */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{
              minHeight: "150px",
              padding: "20px",
            }}
          >
            <div className="spinner-border text-primary" />
          </div>
        ) : (
          <div
            className="card-body"
            style={{
              padding: "8px 16px 10px",
            }}
          >
            <CrudTable
              data={tableData}
              onEdit={openModal}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              columns={[
                "sl",
                "id",
                "brand_image",
                "name",
                "status",
              ]}
            />
          </div>
        )}
      </div>
    </div>
  </DashboardWrapper>
);
}